<template>
    <div
        class="uik-highlight-card"
        :class="[
            `uik-highlight-card--${color}`,
            `uik-highlight-card--${layout}`,
            {'uik-highlight-card--clickable': action}
        ]"
        @click="triggerAction()"
    >
        <div class="uik-highlight-card__content">
            <div class="uik-highlight-card__icon">
                <UikIcon :icon="['fas', icon]"/>
            </div>
            <div class="uik-highlight-card__text">
                <div
                    class="uik-highlight-card__title"
                    :title="title"
                >{{ title }}</div>
                <div
                    class="uik-highlight-card__subtitle"
                    :title="subtitle"
                >{{ subtitle }}</div>
            </div>
        </div>
        <button
            v-if="action && layout === 'horizontal'"
            class="uik-highlight-card__btn"
        >
            <UikIcon :icon="['fas', 'chevron-right']"/>
        </button>
        <Background
            v-if="color !== 'transparent'"
            :type="bg"
            class="uik-highlight-card__pattern"
        />
    </div>
</template>

<script>
import Background from "./Pattern"
export default {
    name: "UikHighlightCard",
    components: { Background },
    props: {
        /**
         * Card color
         */
        color: {
            type: String,
            default: "primary",
            validator (value) {
                return [
                    "transparent",
                    "primary",
                    "secondary",
                    "success",
                    "warning",
                    "error",
                    "red",
                    "orange",
                    "yellow",
                    "lime",
                    "green",
                    "cyan",
                    "blue",
                    "purple",
                    "pink",
                    "gray"
                ].indexOf(value) !== -1
            }
        },
        /**
         * Card icon
         */
        icon: {
            type: String,
            default: ""
        },
        /**
         * Primary text
         */
        title: {
            type: String,
            default: ""
        },
        /**
         * Secondary text
         */
        subtitle: {
            type: String,
            default: ""
        },
        /**
         * Background pattern
         */
        bg: {
            type: [String, Number],
            default: 4,
            validator (value) {
                value = parseFloat(value)
                if (!isNaN(value) && value > 0 && value < 10) return true

                return false
            }
        },
        /**
         * Content layout
         */
        layout: {
            type: String,
            default: "horizontal",
            validator (value) {
                return [
                    "horizontal",
                    "vertical"
                ].indexOf(value) !== -1
            }
        },
        /**
         * Action triggered on click
         */
        action: {
            type: Function,
            default: undefined
        }
    },
    methods: {
        /**
         * Triggers action if defined and emits 'click' event
         */
        triggerAction () {
            this.$emit("click", this.action)

            if (this.action) this.action()
        }
    }
}
</script>

<style lang="scss">
.uik-highlight-card {
    padding: 25px;
    border-radius: 15px;
    width: 100%;
    position: relative;
    display: flex;
    flex-flow: row nowrap;
    justify-content: flex-start;
    align-items: center;
    overflow: hidden;

    @each $name, $color in $colors {
        &--#{"" + $name} {
            background: linear-gradient(darken($color, 0), darken($color, 3%));
            box-shadow: 0 10px 15px -8px rgba($color, 0.5);

            .uik-highlight-card__btn {
                background: lighten($color, 5%);
                z-index: 1;
                &:hover {
                    background: lighten($color, 10%);
                }
                &:active {
                    background: lighten($color, 12%);
                }
            }
        }
    }

    @each $name, $color in $projectColors {
        &--#{"" + $name} {
            background: linear-gradient(darken($color, 0), darken($color, 3%));
            box-shadow: 0 10px 15px -8px rgba($color, 0.5);

            .uik-highlight-card__btn {
                background: lighten($color, 5%);
                z-index: 1;
                &:hover {
                    background: lighten($color, 5%);
                }
                &:active {
                    background: lighten($color, 10%);
                }
            }
        }
    }

    .uik-highlight-card__content {
        display: flex;
        flex-flow: row nowrap;
        justify-content: flex-start;
        align-items: center;
        position: relative;
        z-index: 1;
        max-width: 100%;

        .uik-highlight-card__icon {
            color: white;
            width: 50px;
            min-width: 50px;
            height: 50px;
            border-radius: 99px;
            // border-top-right-radius: 0;
            background: rgba(white, 0.2);
            display: flex;
            flex-flow: row nowrap;
            justify-content: center;
            align-items: center;

            svg {
                font-size: 24px;
                height: 24px;
                width: 24px;
            }
        }

        .uik-highlight-card__text {
            display: flex;
            flex-flow: column nowrap;
            justify-content: center;
            align-items: flex-start;
            margin-left: 20px;
            max-width: calc(100% - 70px);

            .uik-highlight-card__title {
                color: white;
                font-size: 20px;
                font-weight: 600;
                line-height: 24px;
                display: block;
                white-space: initial;
                overflow: hidden;
                text-overflow: ellipsis;
                word-break: break-word;
                max-width: 100%;
            }

            .uik-highlight-card__subtitle {
                color: rgba(white, 0.8);
                font-size: 14px;
                font-weight: 400;
                line-height: 18px;
                margin-top: 2px;
                margin-bottom: 2px;
                display: block;
                white-space: initial;
                overflow: hidden;
                text-overflow: ellipsis;
                word-break: break-word;
                max-width: 100%;
            }
        }
    }

    .uik-highlight-card__btn {
        height: calc(100% - 20px);
        position: absolute;
        right: 10px;
        top: 10px;
        padding: 0 10px;
        display: flex;
        justify-content: center;
        align-items: center;
        border-radius: 8px;
        border: none;
        transition: all 0.125s;
        color: white;
        font-size: 16px;

        &:hover {
            cursor: pointer;
        }
    }

    & + .uik-highlight-card {
        margin-left: 20px;
    }

    &--transparent {
        background: transparent !important;
        border: solid 1px rgba($borderColorDarker, 0.8);
        box-shadow: none;

        .uik-highlight-card__content {
            .uik-highlight-card__icon {
                color: $textSecondary;
                background: $borderColor;
            }

            .uik-highlight-card__text {
                .uik-highlight-card__title {
                    color: $textBase;
                }

                .uik-highlight-card__subtitle {
                    color: rgba($textBase, 0.5);
                    font-weight: 500;
                }
            }
        }

        .uik-highlight-card__btn {
            color: $textSecondary;
            background: $borderColor;

            &:hover {
                background: lighten($borderColorDarker, 2%);
            }

            &:active {
                background: $borderColorDarker;
            }
        }
    }

    &--clickable {
        padding-right: 50px;
        cursor: pointer;
        transition: all 0.125s;

        @each $name, $color in $colors {
            &.uik-highlight-card--#{"" + $name}:hover {
                box-shadow: 0 10px 20px -10px rgba($color, 1);
            }
        }

        @each $name, $color in $projectColors {
            &.uik-highlight-card--#{"" + $name}:hover {
                box-shadow: 0 10px 20px -10px rgba($color, 1);
            }
        }

        &:active {
            transform: scale(0.98);
        }
    }

    &--vertical {
        flex-flow: column nowrap;
        justify-content: center;
        align-items: center;
        padding: 50px 25px;

        .uik-highlight-card__content {
            flex-flow: column nowrap;
            justify-content: center;
            align-items: center;
            width: 100%;

            .uik-highlight-card__text {
                margin-left: 0;
                margin-top: 25px;
                align-items: center;
                max-width: 100%;

                .uik-highlight-card__title,
                .uik-highlight-card__subtitle {
                    text-align: center;
                }
            }
        }
    }
}
</style>
